import { useMemo } from 'react';

import { ByggType, PlasseringAvEnhetIByggVertikalt } from '../../../../../components/registering/utils/registerEnums';
import { Bygningsdetaljer } from '../../../../../types/registration/userInput';

interface UseBoligInformasjonReturn {
  boligtype: string | undefined;
  boenhet: string | undefined;
  byggear: number | undefined;
  antallEtasjer: number | undefined;
  totalBRA: number | undefined;
  totalOppvarmetBRA: number | undefined;
  harKjeller: boolean | undefined;
  harOppvarmetKjeller: boolean | undefined;
  konstruksjonsmateriale: string | undefined;
}

export const useBoligInformasjon = (bygningsdetaljer: Bygningsdetaljer): UseBoligInformasjonReturn => {
  return useMemo(() => {
    const {
      byggType,
      plasseringAvEnhetIByggHorisontalt,
      plasseringAvEnhetIByggVertikalt,
      byggAr,
      antallEtasjerUnntattKjeller,
      totalBRA,
      totalOppvarmetBRA,
      harBoligenKjeller,
      heleEllerDelerAvKjellerErOppvarmet,
      konstruksjonsmateriale
    } = bygningsdetaljer;

    const erLeilighet = byggType === ByggType.Leilighet;
    const erRekkehus = byggType === ByggType.Rekkehus;
    const erHorisontaldeltBolig = byggType
      ? [ByggType.TomannsboligHorisontalDelt, ByggType.Firemannsbolig].includes(byggType)
      : false;

    let boenhet: string | undefined;

    if (erLeilighet) {
      // For Leilighet: Vis både horisontal og vertikal plassering (begge kan være satt)
      // Prioriter vertikal plassering hvis begge finnes
      boenhet = plasseringAvEnhetIByggVertikalt || plasseringAvEnhetIByggHorisontalt;
    } else if (erHorisontaldeltBolig) {
      // For TomannsboligHorisontalDelt og Firemannsbolig: Vis kun vertikal plassering
      boenhet = plasseringAvEnhetIByggVertikalt;
    } else if (erRekkehus) {
      // For Rekkehus: Vis kun horisontal plassering
      boenhet = plasseringAvEnhetIByggHorisontalt;
    }

    const skalViseKjellerInfo = !(
      erLeilighet &&
      plasseringAvEnhetIByggVertikalt != null &&
      plasseringAvEnhetIByggVertikalt !== PlasseringAvEnhetIByggVertikalt.NedersteEtasje
    );

    const harKjeller = skalViseKjellerInfo ? harBoligenKjeller : undefined;

    const harOppvarmetKjeller = harBoligenKjeller ? heleEllerDelerAvKjellerErOppvarmet : undefined;

    return {
      boligtype: byggType,
      boenhet,
      byggear: byggAr,
      antallEtasjer: antallEtasjerUnntattKjeller,
      totalBRA,
      totalOppvarmetBRA,
      harKjeller,
      harOppvarmetKjeller,
      konstruksjonsmateriale
    };
  }, [bygningsdetaljer]);
};
